import { clsx } from 'clsx';
import { CSSProperties, FocusEvent, forwardRef, ReactElement, Ref, useContext, useMemo } from 'react';
import {
  ComboBox as AriaComboBox,
  ComboBoxStateContext,
  FieldError,
  Input,
  Key,
  Label,
  Text,
} from 'react-aria-components';
import IconButton from '~/components/buttons/NewIconButton';
import fieldStyles from '~/components/inputs/Field.module.scss';
import ListBoxPopover from '~/components/inputs/Select/ListBoxPopover';
import useDataTestId from '~/hooks/useDataTestId';
import IconChevron from '~/icons/icon-chevron-down.svg?react';
import IconClose from '~/icons/icon-close.svg?react';
import styles from './ComboBox.module.scss';

export interface ComboBoxOption<T> {
  id: string;
  action?: ReactElement;
  endIcon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  label: string;
  media?: ReactElement;
  description?: string;
  value: T;
  isDisabled?: boolean;
  className?: string;
}

export interface BaseComboBoxProps {
  additionalContent?: ReactElement;
  additionalContentSticky?: boolean;
  headerContent?: ReactElement;
  allowsCustomValue?: boolean;
  EndIcon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  StartIcon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  allowsEmptyCollection?: boolean;
  autoFocus?: boolean;
  className?: string;
  dataTestId?: string;
  emptyState?: ReactElement;
  endAddon?: string;
  errorMessage?: string;
  helpText?: string;
  hideLabel?: boolean;
  id?: string;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  isRequired?: boolean;
  isInvalid?: boolean;
  inputValue?: string;
  label: string;
  maxHeight?: number;
  menuTrigger?: 'focus' | 'input' | 'manual';
  name?: string;
  onFocus?: (e: FocusEvent<Element, Element>) => void;
  onBlur?: (e: FocusEvent<Element, Element>) => void;
  onInputChange?: (value: string) => void;
  placeholder?: string;
  shouldFlip?: boolean;
  size?: 'small' | 'medium' | 'large';
  style?: CSSProperties;
  showChevron?: boolean;
  hasClearButton?: boolean;
  onClear?: () => void;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  defaultFilter?: (textValue: string, inputValue: string) => boolean;
}

interface Props<T> extends BaseComboBoxProps {
  valueToId: (value: T | null) => string;
  options: ComboBoxOption<T>[];
  onChange: (value: T | null) => void;
  value: T | null;
}

const ComboBoxClearButton = ({
  size,
  hasChevron,
  onClear,
}: {
  size?: 'small' | 'medium' | 'large';
  hasChevron: boolean;
  onClear?: () => void;
}) => {
  const state = useContext(ComboBoxStateContext);
  return (
    <IconButton
      Icon={IconClose}
      size={size === 'large' ? 'large' : 'small'}
      variant="ghost"
      className={clsx({ [styles.clearButton!]: hasChevron })}
      onClick={() => (onClear ? onClear() : state?.setSelectedKey(null))}
      data-testid="combobox-clear-button"
    />
  );
};

const ComboBox = <T,>({ size = 'medium', showChevron = true, ...props }: Props<T>, ref: Ref<HTMLDivElement>) => {
  const handleChange = (value: Key | null) => {
    if (!value) {
      return props.onChange(null);
    }
    const selectedOption = props.options.find((option) => option.id === value);
    return props.onChange(selectedOption?.value ?? null);
  };

  const selectedId = props.valueToId(props.value);
  const selectedOption = props.options.find((option) => option.id === selectedId);
  const selectedKey: Key | null = selectedOption ? selectedOption.id : null;

  const { options } = props;
  const listBoxOptions = useMemo(
    () =>
      options.map((option) => ({
        action: option.action,
        className: option.className,
        endIcon: option.endIcon,
        description: option.description,
        id: option.id,
        label: option.label,
        media: option.media,
        textValue: option.label,
        isDisabled: option.isDisabled,
      })),
    [options],
  );

  const dt = useDataTestId(props.dataTestId);
  const hasChevron = !props.isReadOnly && showChevron;
  return (
    <AriaComboBox
      ref={ref}
      data-testid={dt()}
      autoFocus={props.autoFocus}
      allowsCustomValue={props.allowsCustomValue}
      allowsEmptyCollection={props.allowsEmptyCollection}
      aria-label={props.hideLabel ? props.label : undefined}
      className={clsx(fieldStyles.field, styles.comboBoxField, props.className)}
      data-size={size}
      id={props.id}
      inputValue={props.inputValue}
      isDisabled={props.isDisabled}
      isReadOnly={props.isReadOnly}
      isRequired={props.isRequired}
      isInvalid={props.isInvalid || !!props.errorMessage}
      menuTrigger={props.menuTrigger}
      name={props.name}
      onFocus={props.onFocus}
      onBlur={props.onBlur}
      onInputChange={props.onInputChange}
      onSelectionChange={handleChange}
      style={props.style}
      selectedKey={selectedKey}
      validationBehavior="aria"
      // Workaround for react-aria issue https://github.com/adobe/react-spectrum/issues/9233
      {...(props.defaultFilter !== undefined && { defaultFilter: props.defaultFilter })}
    >
      {!props.hideLabel && (
        <Label>
          {props.label} {props.isRequired ? ' *' : null}
        </Label>
      )}

      <div className={styles.addonWrapper}>
        <div className={styles.comboContainer}>
          {(selectedOption?.media || props.StartIcon) && (
            <div className={styles.media}>{selectedOption?.media ?? (props.StartIcon && <props.StartIcon />)}</div>
          )}
          <Input className={styles.valueContainer} placeholder={props.placeholder} />
          {(selectedOption || props.inputValue) && (props.hasClearButton || props.onClear) && (
            <ComboBoxClearButton size={size} hasChevron={hasChevron} onClear={props.onClear} />
          )}
          {!props.isReadOnly && showChevron && (
            <IconButton Icon={IconChevron} size={size === 'large' ? 'large' : 'small'} variant="ghost" />
          )}
        </div>
        {props.endAddon && <div className={clsx(styles.addon, styles.endAddon)}>{props.endAddon}</div>}
        {props.EndIcon && <props.EndIcon className={styles.endIcon} />}
      </div>

      {props.helpText && <Text slot="description">{props.helpText}</Text>}

      <FieldError>{props.errorMessage}</FieldError>

      <ListBoxPopover
        additionalContent={props.additionalContent}
        additionalContentSticky={props.additionalContentSticky}
        headerContent={props.headerContent}
        emptyState={props.emptyState}
        maxHeight={props.maxHeight}
        options={listBoxOptions}
        shouldFlip={props.shouldFlip}
        onLoadMore={props.onLoadMore}
        isLoadingMore={props.isLoadingMore}
      />
    </AriaComboBox>
  );
};

export default forwardRef(ComboBox) as <T>(p: Props<T> & { ref?: Ref<HTMLDivElement> }) => ReactElement;
